import { Accessor, ColorX, GFunc, LocaleX, LogicX, ZTime, ZValidate } from "IZOX/STATIC";
import { HStack, VStack, XButton, XDiv, XText } from "IZOX/_X";
// eslint-disable-next-line no-unused-vars
import React, { CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import QFText from "./QFText";
import { MdClose, MdOutlineCalendarToday } from "react-icons/md";
import { Calendarizo } from "IZOX/BLOCKS/Labs";
import { FaClock } from "react-icons/fa6";
import QFDropdown from "./QFDropdown";
import _ from "lodash";
import QClock from "../../QClock/QClock";

/**
 * @typedef {import("IZOX/_X/XInput").xinputparams} xinputparams
 * @typedef {import("IZOX/_X/XInput").xinputstyleparams} xinputstyleparams
 * @typedef {import("IZOX/STATIC/ZValidate").fvalidate} fvalidate
 * @typedef {import("IZOX/__Typedef/common").lang} lang
 * 
 * @typedef {Object} qfdateparamsaddOns
 * @property {String} name
 * @property {lang} label
 * @property {any} defaultValue
 * @property {Function} _onChange
 * @property {Function} _onSetDefault
 * @property {any} formValue
 * @property {String} error_bColor
 * @property {String} invalidColor
 * @property {Number} calendarSize
 * @property {String} minDate
 * @property {String} maxDate
 * @property {Number} defaultDisYear
 * @property {Number} defaultDisMonth
 * @property {({doc, field, addOns}) => Boolean} isError
 * @property {fvalidate | fvalidate[]} validators
 * @property {"StartOfDay" | "EndOfDay" | "Current"} dateOnlySnap
 * @property {"StartOfMonth" | "EndOfMonth" | "Current"} monthOnlySnap
 * @property {"StartOfYear" | "EndOfYear" | "Current"} yearOnlySnap
 * @property {"year" | "month" | "date" | "datetime" | "time"} dateType
 * @property {Boolean} showMinute
 * @property {Boolean} showSecond
 * @property {Boolean} multiple Date Only
 * @property {Boolean} deleteBtn
 * 
 * @typedef {qfdateparamsaddOns & xinputparams & xinputstyleparams} qfdateparams
 * 
 * @param {qfdateparams} param0
 * @returns 
 */
function QFDate({
  name = undefined,
  label = undefined,
  _onChange = null,
  _onSetDefault = null,
  defaultValue = undefined,
  formValue = {},
  dateFormat = undefined,
  error_bColor = ColorX.CSS("Error"),
  invalidColor = ColorX.CSS("pureRed"),
  calendarSize = 30,
  minDate = undefined,
  maxDate = undefined,
  disabled_bgColor = ColorX.CSS("bgGreyOut"),
  disabled_color = ColorX.CSS("greyOut"),
  defaultDisYear = undefined,
  defaultDisMonth = undefined,
  isError = undefined,
  addOns = undefined,
  updateVia = undefined, 
  updateField = undefined,
  forceUpdateField = undefined,
  performanceBoost = undefined,
  dateOnlySnap = undefined,
  monthOnlySnap = undefined,
  yearOnlySnap = undefined,
  dateType = "date",
  showMinute = true,
  showSecond = false,
  placeholder = undefined,
  multiple = false,
  minStep = 1,
  secStep = 1,
  deleteBtn = false,
  validators = [],
  ...others
}){

  const ref = useRef(null);
  const [display, setDisplay] = useState(defaultValue);
  const [valid, setValid] = useState(false);

  const [, updateState] = React.useState();
  const forceUpdate = React.useCallback(() => updateState({}), []);

  const timeOnly = dateType === "time";

  if(multiple && dateType !== "date"){
    multiple = false;
  }

  const GetDefaultDateFormat = useCallback(() => {
    switch(dateType){
      case "year": return "YYYY";
      case "month": return "MMM YYYY";
      default: case "date": return "DD/MM/YYYY";
      case "datetime": return showSecond ? "DD/MM/YYYY HH:mm:ss" : "DD/MM/YYYY HH:mm";
      case "time": return showSecond ? "HH:mm:ss" : "HH:mm";
    }
  }, [dateType, showSecond]);

  dateFormat = dateFormat ?? GetDefaultDateFormat();
  placeholder = placeholder ?? GetDefaultDateFormat();

  useEffect(() => {
    if(defaultValue) {
      _onSetDefault?.(name, defaultValue);
      let disStr = "";
      try {
        disStr = ZTime.Moment(defaultValue, 
          timeOnly ? dateFormat : undefined
        ).format(dateFormat);
        setValid(true);
      }catch(e){
        disStr = defaultValue;
        setValid(false);
      }
      setDisplay(disStr);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let _value = Accessor.Get(formValue, name);
    if(_.isUndefined(_value)){
      setValid(true);
      setDisplay("");
      return;
    }
    if(_value){
      if(multiple){
        let disStr = _.map(_value, o => ZTime.Moment(o, 
          timeOnly ? dateFormat : undefined
        ).format(dateFormat));
        setDisplay(disStr);
        setValid(true);
      }else if(_value.length > dateFormat.length && dateType === "time"){
        setDisplay(_value);
        setValid(false);
      }else if(_value.length >= dateFormat.length){
        try {
          let mt = ZTime.Moment(_value, timeOnly ? dateFormat : undefined);
          if(mt.isValid()){
            let disStr = mt.format(dateFormat);
            setDisplay(disStr);
            setValid(true);
          }else{
            setDisplay(_value);
            setValid(false);
          }
          
        }catch{
          setDisplay(_value);
          setValid(false);
        }
      }else{
        if(/[^a-zA-Z]/.test(dateFormat[_value.length])){
          _value += dateFormat[_value.length];
        }
        setDisplay(_value);
        setValid(false);
      }
      
    }else{
      setDisplay("");
    }
  }, [formValue, name, dateFormat, multiple, timeOnly, dateType]);

  // eslint-disable-next-line no-unused-vars
  let {bgColor, width, height = 30, bW, bColor, fontSize, color, readOnly, p, ...exOthers} = others;

  let _value = Accessor.Get(formValue, name);
  let _error = !valid;
  if(isError){
    _error = _error || isError({doc: formValue, field: _value, addOns});
  }
  if(!_.isEmpty(validators)){
    _error = _error || !ZValidate.Check(_value, validators);
  }

  const _snapDate = useCallback((value) => {
    if(dateType === "date"){
      switch(dateOnlySnap){
        default: case "StartOfDay": return ZTime.StartOfDay(value).toISOString();
        case "EndOfDay": return ZTime.EndOfDay(value).toISOString();
        case "Current": return ZTime.Moment(value).toISOString();
      }
    }
    if(dateType === "month"){
      switch(monthOnlySnap){
        default: case "StartOfMonth": return ZTime.StartOfMonth(value).toISOString();
        case "EndOfMonth": return ZTime.EndOfMonth(value).toISOString();
        case "Current": return ZTime.Moment(value).toISOString();
      }
    }
    if(dateType === "year"){
      switch(yearOnlySnap){
        default: case "StartOfYear": return ZTime.StartOfYear(value).toISOString();
        case "EndOfYear": return ZTime.EndOfYear(value).toISOString();
        case "Current": return ZTime.Moment(value).toISOString();
      }
    }
    return value;
  }, [dateType, dateOnlySnap, monthOnlySnap, yearOnlySnap]);

  const deleteValue = useCallback((name) => {
    if(readOnly) return;
    if(multiple){
      _onChange(name, []);
    }else{
      _onChange(name, null);
    }
  }, [readOnly, _onChange, multiple]);

  const _modToOnChange = useCallback((name, text) => {
    if(minDate && ZTime.IsBefore(text, minDate)){
      text = minDate;
    }
    if(maxDate && ZTime.IsAfter(text, maxDate)){
      text = maxDate;
    }
    text = _snapDate(text);
    
    _onChange(name, text);
  }, [minDate, maxDate, _onChange, _snapDate]);

  const _modAllToOnChange = useCallback((name, texts) => {
    texts = _.map(texts, o => {
      if(minDate && ZTime.IsBefore(o, minDate)){
        o = minDate;
      }
      if(maxDate && ZTime.IsAfter(o, maxDate)){
        o = maxDate;
      }
      o = _snapDate(o);
      return o;
    });
    _onChange(name, texts);
  }, [minDate, maxDate, _onChange, _snapDate]);

  const onInputChange = useCallback((name, text) => {
    setDisplay(text);
    if(ZTime.IsFormatValid(text, dateFormat)){
      let _v = dateType === "time" ? 
        ZTime.Parse(text, dateFormat).format(dateFormat) : ZTime.Parse(text, dateFormat).toISOString();
      _modToOnChange(name, _v);

      setValid(true);
    }else{
      _onChange(name, text);
      setValid(false);
    }
    forceUpdate?.();
  }, [dateFormat, dateType, _modToOnChange, _onChange, forceUpdate]);

  const onDeleteInput = useCallback((text, e) => {
    e.preventDefault();
    if(!LogicX.isOneOf(dateFormat[text.length - 1], ["H", "h", "m", "M", "D", "Y", "L"])){
      text = text.slice(0, -2);
    }else{
      text = text.slice(0, -1);
    }
    _onChange(name, text);
    setValid(false);
  }, [dateFormat, _onChange, name]);

  const onConfirmYear = useCallback((_name, year) => {
    let _value = Accessor.Get(formValue, name);
    let newValue = ZTime.Moment(_value).set("year", year)
      .toISOString();
    let disStr = ZTime.Moment(newValue).format(dateFormat);
    setDisplay(disStr);
    setValid(true);
    _modToOnChange(name, newValue);
    setTimeout(() => {
      GFunc.CloseSelectorOverlay();
    }, 100);
  }, [formValue, name, dateFormat, _modToOnChange]);

  const onConfirmMonth = (_name, month) => {
    let _value = Accessor.Get(formValue, name);
    let newValue = ZTime.Moment(_value).set("month", month)
      .toISOString();
    let disStr = ZTime.Moment(newValue).format(dateFormat);
    setDisplay(disStr);
    setValid(true);
    _modToOnChange(name, newValue);
    setTimeout(() => {
      GFunc.CloseSelectorOverlay();
    }, 100);
  };

  const onClockConfirm = useCallback((hour, minute, second, mode) => {
    let _value = Accessor.Get(formValue, name);
    if(timeOnly){
      _value = ZTime.Moment().toISOString();
    }
    let newValue = ZTime.Moment(_value).set("hour", hour)
      .set("minute", minute)
      .set("second", second)
      .toISOString();
    let disStr = ZTime.Moment(newValue).format(dateFormat);
    setDisplay(disStr);
    setValid(true);
    if(dateType === "time"){
      newValue = ZTime.Moment(newValue).format(dateFormat);
    };
    _modToOnChange(name, newValue);
    let canClose = showMinute && showSecond && mode === "hour" ? true :
      showMinute && mode === "minute" ? true :
      showSecond && mode === "second" ? true : false;
    if(canClose){
      setTimeout(() => {
        GFunc.CloseSelectorOverlay();
      }, 100);
    }
  }, [formValue, name, dateFormat, timeOnly, showMinute, showSecond,
    dateType, _modToOnChange]);

  const onCalendarConfirm = useCallback((date) => {
    if(readOnly) return;
    if(multiple){
      setDisplay(date);
      _modAllToOnChange(name, date);
      setValid(true);
      setTimeout(() => {
        GFunc.CloseSelectorOverlay();
      }, 100);
    }else{
      let _value = Accessor.Get(formValue, name);
      let oriHour = ZTime.Moment(_value).hour();
      let oriMinute = ZTime.Moment(_value).minute();
      let oriSecond = ZTime.Moment(_value).second();
      date = ZTime.Moment(date).set("hour", oriHour)
        .set("minute", oriMinute)
        .set("second", oriSecond).toISOString();
      let disStr = ZTime.Moment(date).format(dateFormat);
      setDisplay(disStr);
      _modToOnChange(name, date);
      setValid(true);
      setTimeout(() => {
        GFunc.CloseSelectorOverlay();
      }, 100);
    } 
  }, [formValue, name, dateFormat, multiple, readOnly, _modToOnChange, _modAllToOnChange]);

  const openClock = (e) => {
    if(readOnly) return;
    let rect = ref.current.getBoundingClientRect();
    let _v = Accessor.Get(formValue, name) ?? defaultValue;
    if(timeOnly){
      _v = ZTime.Moment(_value, dateFormat).toISOString();
    }

    GFunc.SelectorOverlay(() => {
      return (
        <VStack position="absolute" bW={1}
          round={[0, 0, 5, 5]}
          bColor={ColorX.CSS("greyOut")}
          style={{
            top: rect.bottom,
            left: rect.left
          }}>
          <QClock 
            valDate={_v}
            size={calendarSize * 7}
            directConfirm
            onConfirm={onClockConfirm}
            enableMinute={showMinute}
            enableSecond={showSecond}
            minStep={minStep}
            secStep={secStep}
            />
        </VStack>
      );
    }, {}, true, false, true);
  };

  const openCalendar = (e) => {
    if(readOnly) return;
    let rect = ref.current.getBoundingClientRect();
    GFunc.SelectorOverlay(() => {
      return (
        <VStack position="absolute"
          style={{
            top: rect.bottom,
            left: rect.left,
          }} shadow round={10}>
          <Calendarizo 
            minDate={minDate}
            maxDate={maxDate}
            defaultDisMonth={defaultDisMonth}
            defaultDisYear={defaultDisYear}
            valDate={multiple ? undefined : (_value ?? defaultValue)}
            valDates={multiple ? (_value ?? defaultValue) : undefined}
            size={calendarSize}
            directConfirm
            onConfirm={onCalendarConfirm}
            onCancel={() => GFunc.CloseSelectorOverlay()}
            style={{borderRadius: 10}}
            multiple={multiple}
            />
        </VStack>
      );
    }, {}, true, false, true);
  };

  function renderFullTimeSelector(){
    return (
      <HStack lockHeight={height} {...exOthers} bgColor={readOnly ? 
        (disabled_bgColor ?? bgColor ?? ColorX.CSS("pureWhite")):
        (bgColor ?? ColorX.CSS("pureWhite"))} 
        bW={bW} bColor={!readOnly && _error ? error_bColor : bColor} HAlign="[">
        {multiple ?
          <XButton onPress={openCalendar} flexGrow={1} colorTransition
            disabled={readOnly}
            tooltip={`(${display?.length ?? 0}) ${display?.join?.(", ") ?? ""}`}>
            <XText noWrap ellipsisMode="ellipsis" colorTransition
              color={readOnly? disabled_color : display?.length === 0 ? ColorX.CSS("placeholder") : color}>
              {display?.length === 0 ?
                LocaleX.Parse(placeholder) :
                `(${display?.length ?? 0}) ${display?.join?.(", ") ?? ""}`}
            </XText>
          </XButton>
          :
          <QFText
            {...others}
            disabled_bgColor={disabled_bgColor}
            color={valid ? color : invalidColor}
            name={"display"}
            formValue={{display: display}}
            fontSize={fontSize}
            _onChange={(_, text) => onInputChange(name, text)}
            onDeleteKeyDown={(text, e) => onDeleteInput(text, e)}
            _onSetDefault={_onSetDefault}
            placeholder={placeholder}
            readOnly={readOnly || multiple}
            bW={0}
            flexGrow={1}
            />
          }
        {deleteBtn && !readOnly &&
          <XButton padding={5} lockWidth={height} lockHeight={height} fullHeight
            onPress={() => deleteValue(name)}>
            <MdClose color={ColorX.CSS("greyOut")} size={fontSize}/>
          </XButton>}
        {LogicX.isOneOf(dateType, ["date", "datetime"]) &&
          <XButton padding={5} lockWidth={height} lockHeight={height} fullHeight 
            onPress={openCalendar} disabled={readOnly}>
            <MdOutlineCalendarToday color={ColorX.CSS("greyOut")} size={fontSize + 5}/>
          </XButton>}
        {!multiple && LogicX.isOneOf(dateType, ["time", "datetime"]) &&
          <XButton padding={5} lockWidth={height} lockHeight={height} fullHeight 
            onPress={openClock} disabled={readOnly}>
            <FaClock color={ColorX.CSS("greyOut")} size={fontSize + 5}/>
          </XButton>}
      </HStack>
    );
  }

  function renderShortSelector(){
    let _value = Accessor.Get(formValue, name);
    let curYear = _value ? ZTime.Moment(_value).year() : undefined;
    let disYear = _value ? ZTime.Moment(_value).year() : undefined;
    let disMonth = _value ? ZTime.Moment(_value).month() : undefined;
    let _yearOptions = [];
    let _monthOptions = [];
    let minYear = ZTime.Now().add(-100, "years").year();
    let maxYear = ZTime.Now().add(5, "years").year();
    let minMonth = 0;
    let maxMonth = 11;
    if(minDate){
      minYear = ZTime.Moment(minDate).year();
      minMonth = ZTime.Moment(minDate).month();
    }
    if(maxDate){
      maxYear = ZTime.Moment(maxDate).year();
      maxMonth = ZTime.Moment(maxDate).month();
    }
    _yearOptions = _.map(_.range(minYear, maxYear + 1), o => {
      return {
        cap: String(o),
        val: o
      };
    });

    _yearOptions = _yearOptions.reverse();

    _monthOptions = _.map(_.range(0, 12), o => {
      return {
        cap: ZTime.monthDis.EN.Short[o],
        val: o,
        disabled: 
          (curYear === minYear && o < minMonth) ? true : 
          (curYear === maxYear && o > maxMonth) ? true :
          false
      };
    });
  
    return (
      <HStack {...exOthers} bgColor={readOnly ? 
        (disabled_bgColor ?? bgColor ?? ColorX.CSS("pureWhite")):
        (bgColor ?? ColorX.CSS("pureWhite"))} 
        bW={bW} bColor={!readOnly && _error ? error_bColor : bColor} HAlign="[">
        <QFDropdown
          colorTransition
          width={LogicX.isOneOf(dateType, ["month"]) ? "50%" : "100%"}
          onSelectChange={(value) => onConfirmYear(name, value)}
          options={_yearOptions}
          name={"display"}
          formValue={{display: disYear}}
          placeholder={{EN: "Year", TC: "年"}}
          allowSearch
          readOnly={readOnly}
          />
        {LogicX.isOneOf(dateType, ["month"]) &&
          <QFDropdown
            colorTransition
            width={"50%"}
            onSelectChange={(value) => onConfirmMonth(name, value)}
            options={_monthOptions}
            name={"display"}
            formValue={{display: disMonth}}
            placeholder={{EN: "Month", TC: "月"}}
            readOnly={readOnly}
            />}
      </HStack>  
    );
  }

  function renderMain(){
    switch(dateType){
      case "year":
      case "month": return renderShortSelector();
      default: case "date":
      case "datetime":
      case "time": return renderFullTimeSelector();
    }
  }

  return (
    <XDiv width={width} position="relative" overflow="visible" 
      forwardRef={ref}>
      {renderMain()}
    </XDiv>
  );
}

export default QFDate;